import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useAgent from '../../hooks/useAgent';

dayjs.locale('de');

const PAGE_SIZE = 50;

const defaultFrom = () => dayjs().subtract(7, 'day').format('YYYY-MM-DD');
const defaultTo = () => dayjs().format('YYYY-MM-DD');

const formatDateTime = value => {
  if (!value) return '-';
  const date = dayjs(value);
  return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : '-';
};

const formatNumber = value => Number(value || 0).toLocaleString('de-DE');

const roleLabel = role => {
  switch (role) {
    case 'user':
      return 'User';
    case 'assistant':
      return 'Assistent';
    case 'tool':
      return 'Tool';
    case 'system':
      return 'System';
    default:
      return role || 'Unbekannt';
  }
};

const roleColor = role => {
  switch (role) {
    case 'assistant':
      return 'success';
    case 'tool':
      return 'warning';
    case 'system':
      return 'default';
    default:
      return 'info';
  }
};

const statusColor = status => {
  switch (status) {
    case 'closed':
      return 'success';
    case 'escalated':
      return 'warning';
    default:
      return 'info';
  }
};

const ratingLabel = rating => {
  if (!rating || typeof rating.helpful !== 'boolean') return 'Keine Bewertung';
  return rating.helpful ? 'Hilfreich' : 'Nicht hilfreich';
};

const tokenUsageLabel = tokenUsage => {
  if (!tokenUsage) return 'Tokens: -';
  const total = tokenUsage.total ?? tokenUsage.prompt + tokenUsage.completion;
  return `Tokens: ${formatNumber(total)}`;
};

function AgentChatAuditView() {
  const authCtx = useContext(AuthContext);
  const { fetchAuditSessions, loadAuditSessionMessages } = useAgent();

  const fromRef = useRef(null);
  const toRef = useRef(null);
  const searchRef = useRef(null);

  const [filters, setFilters] = useState({
    from: defaultFrom(),
    to: defaultTo(),
    status: '',
    type: '',
    hasRating: '',
    hasCandidate: '',
  });
  const [skip, setSkip] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  if (!authCtx.checkRight('agent:audit')) {
    return <Navigate to='/welcome' replace />;
  }

  const loadSessions = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await fetchAuditSessions({
        ...filters,
        q: searchRef.current?.value?.trim() || '',
        limit: PAGE_SIZE,
        skip,
      });
      const nextSessions = Array.isArray(response?.sessions) ? response.sessions : [];
      setSessions(nextSessions);
      setTotal(Number(response?.total || 0));
      if (selectedToken && !nextSessions.some(session => session.sessionToken === selectedToken)) {
        setSelectedToken(null);
        setSelectedSession(null);
      }
    } catch (e) {
      toast.error('Fehler beim Laden der Agenten-Chats');
    } finally {
      setListLoading(false);
    }
  }, [fetchAuditSessions, filters, selectedToken, skip]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const loadSessionDetail = useCallback(
    async token => {
      if (!token) return;
      setDetailLoading(true);
      try {
        const response = await loadAuditSessionMessages(token);
        setSelectedSession(response || null);
      } catch (e) {
        toast.error('Fehler beim Laden des Agenten-Chats');
      } finally {
        setDetailLoading(false);
      }
    },
    [loadAuditSessionMessages]
  );

  useEffect(() => {
    if (selectedToken) {
      loadSessionDetail(selectedToken);
    }
  }, [loadSessionDetail, selectedToken]);

  const applyFilters = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      from: fromRef.current?.value || '',
      to: toRef.current?.value || '',
    }));
    setSkip(0);
  }, []);

  const resetFilters = useCallback(() => {
    const from = defaultFrom();
    const to = defaultTo();
    if (fromRef.current) fromRef.current.value = from;
    if (toRef.current) toRef.current.value = to;
    if (searchRef.current) searchRef.current.value = '';
    setFilters({ from, to, status: '', type: '', hasRating: '', hasCandidate: '' });
    setSkip(0);
  }, []);

  const selectedSummary = useMemo(
    () => sessions.find(session => session.sessionToken === selectedToken) || null,
    [selectedToken, sessions]
  );

  const canGoBack = skip > 0;
  const canGoForward = skip + PAGE_SIZE < total;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              xl: 'repeat(9, minmax(0, 1fr))',
            },
            gap: 1.5,
            alignItems: 'stretch',
            '& .MuiTextField-root': {
              minWidth: 0,
              width: '100%',
            },
          }}
        >
          <TextField
            label='Von'
            type='date'
            size='small'
            inputRef={fromRef}
            defaultValue={filters.from}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label='Bis'
            type='date'
            size='small'
            inputRef={toRef}
            defaultValue={filters.to}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label='Status'
            size='small'
            value={filters.status}
            onChange={event => {
              setFilters(prev => ({ ...prev, status: event.target.value }));
              setSkip(0);
            }}
          >
            <MenuItem value=''>Alle</MenuItem>
            <MenuItem value='active'>Aktiv</MenuItem>
            <MenuItem value='escalated'>Eskaliert</MenuItem>
            <MenuItem value='closed'>Geschlossen</MenuItem>
          </TextField>
          <TextField
            select
            label='Typ'
            size='small'
            value={filters.type}
            onChange={event => {
              setFilters(prev => ({ ...prev, type: event.target.value }));
              setSkip(0);
            }}
          >
            <MenuItem value=''>Alle</MenuItem>
            <MenuItem value='chat'>Chat</MenuItem>
            <MenuItem value='voice'>Voice</MenuItem>
          </TextField>
          <TextField
            select
            label='Bewertung'
            size='small'
            value={filters.hasRating}
            onChange={event => {
              setFilters(prev => ({ ...prev, hasRating: event.target.value }));
              setSkip(0);
            }}
          >
            <MenuItem value=''>Alle</MenuItem>
            <MenuItem value='true'>Mit Bewertung</MenuItem>
            <MenuItem value='false'>Ohne Bewertung</MenuItem>
          </TextField>
          <TextField
            select
            label='Buchung'
            size='small'
            value={filters.hasCandidate}
            onChange={event => {
              setFilters(prev => ({ ...prev, hasCandidate: event.target.value }));
              setSkip(0);
            }}
          >
            <MenuItem value=''>Alle</MenuItem>
            <MenuItem value='true'>Identifiziert</MenuItem>
            <MenuItem value='false'>Nicht identifiziert</MenuItem>
          </TextField>
          <TextField
            label='Suche'
            size='small'
            inputRef={searchRef}
            placeholder='Session oder Text'
            onKeyDown={event => {
              if (event.key === 'Enter') applyFilters();
            }}
            sx={{
              gridColumn: {
                xs: '1',
                sm: '1 / -1',
                md: 'span 2',
                xl: 'span 2',
              },
            }}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1,
              gridColumn: { xs: '1', sm: '1 / -1', md: 'span 1', xl: 'span 1' },
              minWidth: 0,
            }}
          >
            <Button variant='contained' onClick={applyFilters} disabled={listLoading} fullWidth>
              Anwenden
            </Button>
            <Button variant='outlined' onClick={resetFilters} disabled={listLoading} fullWidth>
              Zurücksetzen
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 420px) minmax(0, 1fr)' },
          gap: 2,
          minHeight: 'calc(100vh - 220px)',
          minWidth: 0,
        }}
      >
        <Paper sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box>
              <Typography variant='h6' fontWeight={700}>
                Agenten-Chat Audit
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {formatNumber(total)} Sessions
              </Typography>
            </Box>
            {listLoading && <CircularProgress size={22} />}
          </Box>

          <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
            {sessions.map(session => (
              <React.Fragment key={session.sessionToken}>
                <ListItemButton
                  selected={selectedToken === session.sessionToken}
                  onClick={() => setSelectedToken(session.sessionToken)}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant='body2' fontWeight={700} noWrap>
                          {session.sessionToken}
                        </Typography>
                        <Chip
                          size='small'
                          label={session.status || 'active'}
                          color={statusColor(session.status)}
                          variant='outlined'
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                        <Typography variant='caption' color='text.secondary'>
                          {formatDateTime(session.lastMessageAt)} - {session.messageCount || 0}{' '}
                          Nachrichten - {ratingLabel(session.rating)}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                          }}
                        >
                          {session.preview || 'Keine Vorschau'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {session.hasCandidate && (
                            <Chip size='small' label='Buchung' variant='outlined' />
                          )}
                          {session.toolCount > 0 && (
                            <Chip size='small' label={`${session.toolCount} Tools`} variant='outlined' />
                          )}
                          <Chip size='small' label={tokenUsageLabel(session.tokenUsage)} variant='outlined' />
                        </Box>
                      </Box>
                    }
                  />
                </ListItemButton>
                <Divider />
              </React.Fragment>
            ))}
            {!listLoading && sessions.length === 0 && (
              <Box p={3} textAlign='center'>
                <Typography variant='body2' color='text.secondary'>
                  Keine Agenten-Chats im ausgewählten Zeitraum.
                </Typography>
              </Box>
            )}
          </List>

          <Box
            sx={{
              p: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Button size='small' disabled={!canGoBack || listLoading} onClick={() => setSkip(skip - PAGE_SIZE)}>
              Zurück
            </Button>
            <Typography variant='caption' color='text.secondary'>
              {total === 0 ? '0' : `${skip + 1}-${Math.min(skip + PAGE_SIZE, total)}`} von{' '}
              {formatNumber(total)}
            </Typography>
            <Button
              size='small'
              disabled={!canGoForward || listLoading}
              onClick={() => setSkip(skip + PAGE_SIZE)}
            >
              Weiter
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 500 }}>
          {!selectedToken ? (
            <Box p={3}>
              <Typography variant='body2' color='text.secondary'>
                Wähle links eine Session aus.
              </Typography>
            </Box>
          ) : detailLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent='space-between'>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant='h6' fontWeight={700}>
                      Session Details
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ wordBreak: 'break-all' }}>
                      {selectedToken}
                    </Typography>
                  </Box>
                  <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                    <Chip
                      size='small'
                      label={selectedSession?.status || selectedSummary?.status || 'active'}
                      color={statusColor(selectedSession?.status || selectedSummary?.status)}
                      variant='outlined'
                    />
                    <Chip
                      size='small'
                      label={ratingLabel(selectedSession?.rating || selectedSummary?.rating)}
                      variant='outlined'
                    />
                    {(selectedSession?.hasCandidate || selectedSummary?.hasCandidate) && (
                      <Chip size='small' label='Buchung identifiziert' variant='outlined' />
                    )}
                  </Stack>
                </Stack>
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1,
                  }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    Erstellt: {formatDateTime(selectedSession?.createdAt || selectedSummary?.createdAt)}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Letzte Nachricht:{' '}
                    {formatDateTime(selectedSession?.lastMessageAt || selectedSummary?.lastMessageAt)}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Erstellt von: {selectedSession?.createdByName || selectedSummary?.createdByName || '-'}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Intent: {selectedSession?.lastBookingIntentType || selectedSummary?.lastBookingIntentType || '-'}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Abusive Strikes:{' '}
                    {selectedSession?.abusiveStrikeCount ?? selectedSummary?.abusiveStrikeCount ?? 0}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {tokenUsageLabel(
                      selectedSession?.performanceSummary?.tokenUsage || selectedSummary?.tokenUsage
                    )}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(selectedSession?.toolSequence || selectedSummary?.recentTools)?.length ? (
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.04)' }}>
                    <Typography variant='subtitle2' fontWeight={700} gutterBottom>
                      Tools
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {(selectedSession?.toolSequence || selectedSummary?.recentTools || []).map((tool, index) => (
                        <Chip key={`${tool}-${index}`} size='small' label={tool} variant='outlined' />
                      ))}
                    </Box>
                  </Paper>
                ) : null}

                {selectedSession?.performanceSummary?.modelCalls?.length ? (
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.04)' }}>
                    <Typography variant='subtitle2' fontWeight={700} gutterBottom>
                      Modellaufrufe
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {selectedSession.performanceSummary.modelCalls.map((call, index) => (
                        <Typography key={`${call?.model || 'model'}-${index}`} variant='caption' color='text.secondary'>
                          {call?.purpose || 'unknown'} - {call?.model || 'unknown'} -{' '}
                          {formatNumber(call?.usage?.total_tokens || 0)} Tokens -{' '}
                          {formatNumber(call?.tookMs || 0)} ms
                        </Typography>
                      ))}
                    </Box>
                  </Paper>
                ) : null}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(selectedSession?.messages || []).map((message, index) => (
                    <Paper
                      key={`${message?.createdAt || ''}-${index}`}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: '1px solid rgba(255,255,255,0.08)',
                        bgcolor:
                          message.role === 'assistant'
                            ? 'rgba(16,163,127,0.12)'
                            : message.role === 'tool'
                              ? 'rgba(255,152,0,0.10)'
                              : message.role === 'system'
                                ? 'rgba(255,255,255,0.03)'
                                : 'rgba(33,150,243,0.10)',
                      }}
                    >
                      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                        <Chip
                          size='small'
                          label={roleLabel(message.role)}
                          color={roleColor(message.role)}
                          variant='outlined'
                        />
                        {message.senderName && (
                          <Typography variant='caption' color='text.secondary'>
                            {message.senderName}
                          </Typography>
                        )}
                        <Typography variant='caption' color='text.secondary'>
                          {formatDateTime(message.createdAt)}
                        </Typography>
                        {typeof message.aiMS === 'number' && (
                          <Typography variant='caption' color='text.secondary'>
                            AI {formatNumber(message.aiMS)} ms
                          </Typography>
                        )}
                        {typeof message.backendMS === 'number' && (
                          <Typography variant='caption' color='text.secondary'>
                            Backend {formatNumber(message.backendMS)} ms
                          </Typography>
                        )}
                      </Stack>
                      <Typography
                        variant='body2'
                        sx={{ mt: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {message.content || '-'}
                      </Typography>
                      {Array.isArray(message.toolSequence) && message.toolSequence.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                          {message.toolSequence.map((tool, toolIndex) => (
                            <Chip key={`${tool}-${toolIndex}`} size='small' label={tool} variant='outlined' />
                          ))}
                        </Box>
                      )}
                    </Paper>
                  ))}
                  {selectedSession && (!selectedSession.messages || selectedSession.messages.length === 0) && (
                    <Typography variant='body2' color='text.secondary'>
                      Keine Nachrichten vorhanden.
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default AgentChatAuditView;
